import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyName,
  usePropertyAddressUrl,
  usePrimaryColor,
  useSocialLinks,
  useCollectionBinding,
  usePage,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getContrastTextColor, TEXT_ON_PRIMARY_LIGHT, TEXT_ON_PRIMARY_DARK } from '../utils/luminance';
// The parent blue-haven theme pulls its drawer social icons from a company
// `@vibesites/assets` package that isn't declared as a dependency of this
// fork (and isn't in the platform package reservoir either). FontAwesome
// brand icons *are* in the reservoir, so we use them directly — visually a
// wash vs. the parent's inline SVGs, and keeps the drawer's Facebook /
// Instagram / etc. icons working when this override replaces the parent
// Header for this Weidner property.
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faInstagram,
  faXTwitter,
  faYoutube,
  faLinkedinIn,
  faTiktok,
  type IconDefinition,
} from '@fortawesome/free-brands-svg-icons';
// The runtime doesn't expose page metadata (position / parent / template)
// through a hook. `vibesites-pages.json` is the full inheritance chain merged
// by the deploy pipeline; this site's `pages.json` is layered on top so local
// pages show up even before the generated file is rebuilt.
import generatedPagesData from '../vibesites-pages.json';
import localPagesData from '../pages.json';

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// This header intentionally re-implements the nav-page filter locally rather
// than importing `_navShared.buildNavLinks` from the parent theme so the
// nav-content rules can diverge from blue-haven's default while the surrounding
// header layout (single-bar surface, drawer-based mobile menu, dual CTAs)
// stays in lock-step with the parent.
//
// Content differences from the parent's default:
//   • `home` is included as an explicit nav link (Weidner branding surfaces
//     Home in the row alongside Floor Plans / Amenities / ...).
//   • `contact` is included as the last nav link (parent promotes it to a CTA).
//   • `apply-now` and `resident-access` are excluded from the row — they
//     surface as the header CTAs (and again in the mobile drawer).
//   • Three "synthetic" dropdowns group related pages under a single label:
//       - Find Your Home  → Floor Plans + Interactive Map
//       - Features        → Amenities + Pet Policy + Future Residents Club
//       - Gallery & Tours → Gallery + Virtual Tour + Schedule a Tour
//       - Deals           → every page whose path is under `/deals/`
//     Synthetic groups collapse to their single surviving child when the
//     property doesn't have the others (e.g. no interactive map), except
//     Deals, which always stays a dropdown.
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  deleted_at?: string | null;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`, or
      when a synthetic group defines child aliases explicitly. */
  children?: NavLink[];
}

interface NavGroup {
  label: string;
  aliases: string[];
  /** Render as a dropdown even when only one child survives. */
  alwaysDropdown?: boolean;
}

const NAV_EXCLUDED_ALIASES = new Set<string>([
  'privacy-policy',
  'sitemap',
  'accessibility',
  // Apply Now + Resident Access are header CTAs (not nav-row links).
  'apply-now',
  'thank-you',
  'resident-access',
  // `schedule-a-tour` used to be excluded here (the CTA in earlier Rise
  // designs surfaced it), but Weidner branding wants it inside the
  // "Gallery & Tours" dropdown alongside Gallery + Virtual Tour, so it
  // participates in the eligible-pages list below and the synthetic group
  // pulls it in.
  'hyperlocal',
  'reviews',
  // `contact` is intentionally NOT excluded — Contact Us is the last
  // top-level nav link. Apply Now / Resident Access hold the CTA slot.
]);

const SYNTHETIC_NAV_GROUPS: NavGroup[] = [
  { label: 'Find Your Home', aliases: ['floor-plans', 'interactive-map'] },
  { label: 'Features', aliases: ['amenities', 'pet-policy', 'future-residents-club'] },
  { label: 'Gallery & Tours', aliases: ['gallery', 'virtual-tour', 'schedule-a-tour'] },
];

const DEALS_PATH_PREFIX = '/deals/';

function mergePagesData(
  base: Record<string, PageEntry>,
  local: Record<string, Partial<PageEntry>>,
): Record<string, PageEntry> {
  const merged: Record<string, PageEntry> = { ...base };
  for (const [alias, override] of Object.entries(local)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...override } as PageEntry;
  }
  return merged;
}

const pagesData = mergePagesData(
  generatedPagesData as unknown as Record<string, PageEntry>,
  localPagesData as unknown as Record<string, Partial<PageEntry>>,
);

const DEALS_NAV_GROUP: NavGroup = {
  label: 'Deals',
  aliases: Object.entries(pagesData)
    .filter(([, page]) => page.path?.startsWith(DEALS_PATH_PREFIX))
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([alias]) => alias),
  alwaysDropdown: true,
};

const NAV_GROUPS: NavGroup[] = [...SYNTHETIC_NAV_GROUPS, DEALS_NAV_GROUP];

const SYNTHETIC_GROUP_ALIASES = new Set(NAV_GROUPS.flatMap((group) => group.aliases));

/** Normalize a pathname for active-link comparison: drop the query/hash and
    trailing slashes so `/floor-plans`, `/floor-plans/`, and
    `/floor-plans?x=1` all match the `/floor-plans` nav href. Root stays `/`. */
export function normalizePath(p: string): string {
  const path = (p || '').split(/[?#]/)[0];
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 *
 * `hasInteractiveLeasing` gates the Interactive Map page — properties that
 * don't have a configured map provider (ApartmentSync, Engrain SightMap,
 * or the editor override) don't get the map page in the nav, and the
 * synthetic Find Your Home dropdown collapses to plain Floor Plans.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.deleted_at) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];
  const consumedAliases = new Set<string>();

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  // Second pass: attach any pages that still use a real pages.json `parent`.
  // Synthetic groups below own their aliases explicitly so they don't inherit
  // parent relationships like `interactive-map -> floor-plans`.
  for (const [alias, page] of eligible) {
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;
    if (page.parent && SYNTHETIC_GROUP_ALIASES.has(page.parent)) continue;
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    }
  }

  // Third pass: build the top-level nav in page position order, inserting
  // synthetic dropdowns where this Weidner header needs grouped labels that
  // don't correspond to real pages. Each synthetic group is ANCHORED to
  // the FIRST alias in its `aliases` array — that alias's page position is
  // what places the group in the nav row. Earlier revisions triggered the
  // group insertion on ANY member alias, which put Gallery & Tours before
  // Features whenever `virtual-tour` (position 3) sorted ahead of
  // `amenities` (position 4). Anchoring to the first alias makes the row
  // order Weidner-controlled (via aliases[0]) rather than an accident of
  // pages.json positions.
  const emittedGroups = new Set<NavGroup>();
  const emitSyntheticGroup = (group: NavGroup) => {
    if (emittedGroups.has(group)) return;
    emittedGroups.add(group);
    const children = group.aliases
      .map((childAlias) => linksByAlias.get(childAlias))
      .filter((link): link is NavLink => !!link);
    for (const child of children) {
      if (child.alias) consumedAliases.add(child.alias);
    }
    if (children.length === 1 && !group.alwaysDropdown) {
      topLevel.push(children[0]);
    } else if (children.length > 0) {
      topLevel.push({ label: group.label, children });
    }
  };

  for (const [alias, page] of eligible) {
    if (consumedAliases.has(alias)) continue;

    // Anchor alias — emit the group at THIS position.
    const anchoredGroup = NAV_GROUPS.find((item) => item.aliases[0] === alias);
    if (anchoredGroup) {
      emitSyntheticGroup(anchoredGroup);
      continue;
    }

    // Non-anchor member of a synthetic group: hold — don't emit it
    // standalone at this position. It will be pulled into the group when
    // the anchor is encountered later in the walk. The sweep below covers
    // the edge case where the anchor itself was filtered out.
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;

    const parent = page.parent;
    if (parent && eligibleAliases.has(parent) && parent !== alias) continue;

    topLevel.push(linksByAlias.get(alias)!);
  }

  // Fallback sweep: emit any synthetic group whose anchor alias was
  // filtered out entirely (e.g. `floor-plans` hidden on a property). Falls
  // through to the end of the row rather than leaving surviving members
  // silently orphaned.
  for (const group of NAV_GROUPS) {
    emitSyntheticGroup(group);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Logo shown at the top of the tablet / mobile drawer, replacing the plain
// property-name wordmark. Defaults to the property's PRIMARY logo — the same
// full wordmark the top-bar header and footer use — rather than the mono-white
// mark, which for some properties is just a brand glyph (e.g. a paw) and reads
// as a stray icon instead of the property's logo. The drawer sits on
// `--vibe-color-primary` (the dark brand color); when a property's primary
// color is light enough that on-primary text is dark, the runtime swaps this
// default to the mono BLACK variant (see `runtimeDrawerLogoSlotDef` in the
// component) so the mark stays legible on the correct contrast side.
const drawerLogoSlotDef: SlotGroupDefinition = {
  label: 'Drawer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_white_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Primary header CTAs — Apply Now + Resident Access (matched filled pills).
// Contact Us lives in the nav row as the last page link instead.
const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      defaultValue: '/resident-access',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================


// Dot-separated format (516.916.6886) to match the drawer / footer treatment.
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
}

function hasValue(val: string | null | undefined): val is string {
  return !!val && val.trim() !== '';
}

/** True when a flattened custom field on the property is a non-empty string. */
function hasNonEmptyField(property: Record<string, unknown> | null | undefined, key: string): boolean {
  const value = property?.[key];
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * A property "has an interactive map" when any supported map provider is
 * configured. ApartmentSync needs both IDs (a lone JS token is a
 * half-configured state). Engrain SightMap needs any one of its id/url
 * fields — Weidner properties commonly use these instead of ApartmentSync.
 */
function propertyHasInteractiveMap(property: Record<string, unknown> | null | undefined): boolean {
  const hasApartmentSync =
    hasNonEmptyField(property, 'custom_apartmentsync_javascript_id') &&
    hasNonEmptyField(property, 'custom_apartmentsync_config_id');
  const hasEngrainSightmap = [
    'custom_engrain_sightmap_url',
    'custom_engrain_sightmap_id',
    'custom_engrain_sitemap_url',
    'custom_engrain_sitemap_id',
  ].some((key) => hasNonEmptyField(property, key));
  return hasApartmentSync || hasEngrainSightmap;
}

/**
 * Splits a single-line address into a street line and a "city, state zip" line
 * so the drawer address renders on two lines (matching the reference). Drops a
 * trailing country segment. Falls back to a single line when there are no commas.
 */
function splitAddressLines(full: string | null | undefined): { line1: string; line2: string } {
  const cleaned = (full || '')
    .trim()
    .replace(/,?\s*(usa|u\.s\.a\.|united states(?: of america)?)\.?\s*$/i, '')
    .trim();
  const parts = cleaned.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return { line1: cleaned, line2: '' };
  return { line1: parts[0], line2: parts.slice(1).join(', ') };
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// Close (X) icon for the mobile / tablet drawer's circular close button.
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ============================================================================
// Header nav logo: keep the red bar, paint the rest black
// ----------------------------------------------------------------------------
// The property primary logo is a white wordmark + a red bar with a white
// label. A global invert (`brightness(0) invert(1)`) washes the bar to
// white; swapping to `property_mono_black_logo` flattens the bar to black.
// This filter keeps saturated-red pixels (and the white type sitting
// inside the bar) as-is, and recolors every other opaque pixel black so
// the wordmark reads on the light `--vibe-color-surface` header.
// ============================================================================

const HEADER_NAV_LOGO_FILTER_ID = 'header-nav-logo-keep-red';

function HeaderNavLogoFilter() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={0}
      height={0}
      className="absolute overflow-hidden pointer-events-none"
      style={{ position: 'absolute', width: 0, height: 0 }}
    >
      <filter
        id={HEADER_NAV_LOGO_FILTER_ID}
        colorInterpolationFilters="sRGB"
        x="-5%"
        y="-5%"
        width="110%"
        height="110%"
      >
        {/* Alpha = R - 0.5G - 0.5B - 0.1 → opaque on red, empty on white/black. */}
        <feColorMatrix
          in="SourceGraphic"
          type="matrix"
          values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 -0.5 -0.5 0 -0.1"
          result="redScore"
        />
        <feComponentTransfer in="redScore" result="redMask">
          <feFuncA type="discrete" tableValues="0 1" />
        </feComponentTransfer>
        {/* Close the mask so white letters inside the bar stay with the bar
            (dilate fills the holes, erode restores the bar's outer edge). */}
        <feMorphology in="redMask" operator="dilate" radius="6" result="redDilated" />
        <feMorphology in="redDilated" operator="erode" radius="6" result="redClosed" />
        <feComposite in="SourceGraphic" in2="redClosed" operator="in" result="keptRedBar" />
        <feComposite in="SourceGraphic" in2="redClosed" operator="out" result="outsideRed" />
        {/* RGB → black, alpha preserved: white wordmark becomes black. */}
        <feColorMatrix
          in="outsideRed"
          type="matrix"
          values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
          result="blackOutside"
        />
        <feMerge>
          <feMergeNode in="blackOutside" />
          <feMergeNode in="keptRedBar" />
        </feMerge>
      </filter>
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link, currentPath }: { link: NavLink; currentPath: string }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // A link is "active" (persistent double-bar) when the visitor is on its
  // page — or, for a parent with a dropdown, on any of its child pages — so
  // the top + bottom lines that normally appear on hover stay lit to show
  // which interior/banner page is currently in view. `currentPath` comes from
  // the runtime page context (see DesktopNav), so it's correct at render time.
  const childIsActive =
    link.children?.some((child) => !!child.href && normalizePath(child.href) === currentPath) ?? false;
  const isActive =
    !!currentPath &&
    ((!!link.href && normalizePath(link.href) === currentPath) || childIsActive);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        aria-current={isActive ? 'page' : undefined}
        className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 whitespace-nowrap${
          isActive ? ' nav-tb-active' : ''
        }`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Synthetic group labels don't have their own page (no href), so they
          render as a chevron-only trigger button — clicking the label still
          opens the dropdown but doesn't navigate. Real parent pages keep the
          <a> so clicking the label navigates to that page and the chevron +
          dropdown surface the child pages on hover/focus. */}
      {link.href ? (
        <a
          href={link.href}
          aria-current={isActive ? 'page' : undefined}
          className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 flex items-center gap-1 whitespace-nowrap${
            isActive ? ' nav-tb-active' : ''
          }`}
          aria-haspopup="true"
          aria-expanded={open}
          onFocus={() => setOpen(true)}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          onFocus={() => setOpen(true)}
          className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 flex items-center gap-1 whitespace-nowrap bg-transparent${
            isActive ? ' nav-tb-active' : ''
          }`}
          aria-haspopup="true"
          aria-expanded={open}
        >
          {link.label}
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href || child.label}
            href={child.href}
            aria-current={!!child.href && normalizePath(child.href) === currentPath ? 'page' : undefined}
            className="rise-nav-darken-hover block px-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text)] transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Current page path from the runtime context — the authoritative source for
  // "which page am I on". Using `usePage()` (rather than
  // `window.location.pathname`) means the active nav link (persistent
  // double-bar) is correct at SSR/first paint AND inside the editor/preview
  // iframe, whose browser URL is NOT the site's clean page path.
  const currentPath = normalizePath(usePage()?.path || '');

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  // Light the "More" button's double-bar when the current page has been
  // pushed into the overflow dropdown, so the active-page indicator survives
  // at narrow widths instead of silently disappearing with the link.
  const moreActive =
    !!currentPath &&
    overflowLinks.some(
      (link) =>
        (!!link.href && normalizePath(link.href) === currentPath) ||
        (link.children?.some((child) => !!child.href && normalizePath(child.href) === currentPath) ?? false),
    );

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href || link.label}
            className="font-body text-[15px] font-normal tracking-normal inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href || link.label} link={link} currentPath={currentPath} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // synthetic "Find Your Home" parent dropdown so the two
            // affordances feel identical. The click handler stays as a
            // fallback for touch devices where hover doesn't fire, and
            // the click-outside listener still dismisses the menu when
            // the pointer leaves the group entirely.
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 flex items-center gap-1 whitespace-nowrap${
                moreActive ? ' nav-tb-active' : ''
              }`}
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href || link.label}>
                  {link.href ? (
                    <a
                      href={link.href}
                      aria-current={normalizePath(link.href) === currentPath ? 'page' : undefined}
                      className="rise-nav-darken-hover block px-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text)] transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    // Synthetic group labels have no page of their own; keep
                    // them as an unlinked heading so the child rows below
                    // still read as grouped under that label when the group
                    // is pushed into overflow.
                    <div className="block px-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text)]">
                      {link.label}
                    </div>
                  )}
                  {link.children?.map((child) => (
                    <a
                      key={child.href || child.label}
                      href={child.href}
                      aria-current={!!child.href && normalizePath(child.href) === currentPath ? 'page' : undefined}
                      // Indent child rows so the More dropdown reads as a
                      // nested list when a parent gets pushed into overflow
                      // and we want its sub-items to remain reachable from
                      // the dropdown.
                      className="rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text-muted)] transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  /** Child (sub-nav) rows render one step smaller and muted so a parent's
      sub-items read as nested without an indent that looks misaligned in a
      vertical list. */
  isChild?: boolean;
}) {
  // Synthetic group labels have no page of their own — render them as an
  // unlinked heading row so the drawer still reads as grouped, without
  // navigating to a broken URL when tapped.
  if (!link.href) {
    return (
      <div
        className={`block py-2.5 font-body font-normal leading-tight ${
          isChild
            ? 'text-[16px] text-[var(--vibe-color-text-on-primary)]/70'
            : 'text-[20px] text-[var(--vibe-color-text-on-primary)]'
        }`}
      >
        {link.label}
      </div>
    );
  }

  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={`rise-nav-darken-hover block py-2.5 font-body font-normal leading-tight transition-colors ${
        isChild
          ? 'text-[16px] text-[var(--vibe-color-text-on-primary)]/70'
          : 'text-[20px] text-[var(--vibe-color-text-on-primary)]'
      }`}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | Apply Now + Resident
// Access CTAs). The old 3-bar stack (banner + utility bar + nav bar) is
// replaced by one unified bar. CTAs sit on the right of the nav bar on
// desktop / tablet, and fold into the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // configured map provider. ApartmentSync (Repli360 / RRAC) needs BOTH
  // the JavaScript ID and Config ID; Engrain SightMap needs any of its
  // id/url custom fields. A half-configured ApartmentSync property
  // (e.g. Centre Point) must stay hidden. Custom fields are flattened
  // onto the property object, so we read them via `useProperty()` rather
  // than `usePropertyField` (which only knows built-in scalars).
  const propertyForLeasing = useProperty() as Record<string, unknown>;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode) or given a literal
  // string (Custom mode). useContentToken returns the already-RESOLVED
  // value, so a non-empty result exposes the page. ADDITIVE to the
  // provider detection above.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    propertyHasInteractiveMap(propertyForLeasing) || hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // ---- Surface / background contrast decisions ----
  // The header bar is painted with --vibe-color-surface, and its dropdown
  // panels with --vibe-color-background. Unlike primary/secondary, NEITHER of
  // those tokens has an auto-computed contrast color (the tokens generator only
  // derives text-on-primary / text-on-secondary), and both are freely
  // property-overridable. Some properties set a DARK surface/background (e.g.
  // American Landmark's Evolv), which turns the previously hardcoded
  // --vibe-color-text nav illegible. So mirror the same contrast decision the
  // drawer already makes for the primary color: when the surface (or the
  // dropdown's background) is dark, flip the foreground to white; when it's
  // light, keep the theme's own dark text so existing light-surface properties
  // render exactly as before. The header logo is the original full-color
  // mark (red bar + white text, black wordmark) and is left unfiltered.
  const themeColors = useTokens().color;
  const darkTextOnSurface =
    getContrastTextColor(themeColors.surface) === TEXT_ON_PRIMARY_DARK;
  const darkTextOnBackground =
    getContrastTextColor(themeColors.background) === TEXT_ON_PRIMARY_DARK;
  // Foreground colors exposed to the nav CSS as local custom properties (set on
  // <header> below). On a light surface these resolve to the theme's own dark
  // text token — preserving current behavior; on a dark surface they flip white.
  const headerFg = darkTextOnSurface ? 'var(--vibe-color-text)' : TEXT_ON_PRIMARY_LIGHT;
  const dropdownFg = darkTextOnBackground ? 'var(--vibe-color-text)' : TEXT_ON_PRIMARY_LIGHT;

  // Slot groups
  // Original full-color mark (`property_primary_logo` by default). On a light
  // header the wordmark is white-on-white, so `HeaderNavLogoFilter` keeps the
  // red bar + its white label and paints every other opaque pixel black.
  const logo = useSlotGroup('header.logo', logoSlotDef);

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Drawer brand mark — a property logo renders at the top of the drawer,
  // linking home. Defaults to the property's primary logo (its full wordmark),
  // NOT the mono-white mark, which for some properties is only a brand glyph
  // (a paw) and would read as a stray icon. The drawer sits on
  // `--vibe-color-primary`, so mirror the runtime's own contrast decision (the
  // one that drives `--vibe-color-text-on-primary`): keep the primary logo when
  // on-primary is light, and swap to the mono BLACK logo when a light primary
  // color flips on-primary text to dark, so the mark stays legible. The
  // property name is kept as alt text + text fallback when no logo exists.
  const propertyName = usePropertyName();
  const primaryColor = usePrimaryColor();
  const useWhiteOnPrimary = getContrastTextColor(primaryColor) === TEXT_ON_PRIMARY_LIGHT;
  const runtimeDrawerLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? drawerLogoSlotDef
    : {
        ...drawerLogoSlotDef,
        fields: {
          ...drawerLogoSlotDef.fields,
          image: {
            ...drawerLogoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };
  const drawerLogo = useSlotGroup('header.drawer_logo', runtimeDrawerLogoSlotDef);

  // Two-line address for the drawer footer block.
  const { line1: addressLine1, line2: addressLine2 } = splitAddressLines(addressLink.address);

  // Social links — sourced from property fields; only configured platforms
  // render, each as a circular outlined icon on the dark drawer background.
  const social = useSocialLinks();
  const socialLinks: Array<{ url: string; icon: IconDefinition; label: string }> = [
    { url: social.facebook, icon: faFacebookF, label: 'Facebook' },
    { url: social.instagram, icon: faInstagram, label: 'Instagram' },
    { url: social.twitter, icon: faXTwitter, label: 'Twitter' },
    { url: social.youtube, icon: faYoutube, label: 'YouTube' },
    { url: social.linkedin, icon: faLinkedinIn, label: 'LinkedIn' },
    { url: social.tiktok, icon: faTiktok, label: 'TikTok' },
  ].filter((s): s is { url: string; icon: IconDefinition; label: string } => hasValue(s.url));

  // Font loading is handled by the platform, not this theme: each property's
  // CMS picks heading/body fonts from the shared registry (SUPPORTED_FONTS),
  // font-bake injects the selected font on published sites, and the
  // editor/preview index.html preloads every supported family. The
  // --vibe-font-heading / --vibe-font-body tokens (see tokens.ts) then decide
  // which of those loaded families each element renders in. So we deliberately
  // do NOT hardcode a Google Fonts <link> here — doing so would force one
  // family regardless of the property's token selection.

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* Solid header — an opaque --vibe-color-surface band fixed to the top of
          the viewport, matching the reference design. It overlays the top of
          the hero (the full-bleed image continues below the bar), and interior
          `HeroSolidBG` pages already pad their content down to clear it. The nav
          text + logo pick their contrast side from the surface color via the
          `--bh-header-fg` / `--bh-header-dropdown-fg` custom properties below,
          so they stay legible whether the surface is light (cream) or dark. */}
      <header
        className="header-solid fixed left-0 right-0 z-50 w-full"
        style={{
          top: 0,
          backgroundColor: 'var(--vibe-color-surface)',
          '--bh-header-fg': headerFg,
          '--bh-header-dropdown-fg': dropdownFg,
        } as React.CSSProperties}
      >
        {/* Editor-only override. Automatic ApartmentSync / Engrain detection
            already exposes the Interactive Map page; this token is an extra
            signal for properties that use a different field. Rendered
            visually hidden — configuration, not display copy — but wrapped
            in EditableToken so the editor sidebar can reach it. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Optional extra signal to expose the Interactive Map page in the Find Your Home dropdown. ApartmentSync and Engrain SightMap fields are detected automatically; bind this token to another field (or enter a literal) only when you need a custom override. Leave blank to rely on automatic detection."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* Single clean bar: logo · nav links · Apply Now + Resident Access
              CTAs. The phone + address utility row was removed to match the
              reference design; the phone and address remain available in the
              mobile drawer and the footer. */}
          <div className="px-4 sm:px-6 lg:px-8">
            {/* `relative` so the logo can be absolutely centered in the
                collapsed (below-lg) layout: hamburger left · logo centered ·
                CTAs right. On lg+ the hamburger is hidden and the logo
                returns to the left of the centered nav links. */}
            <div className="relative flex items-center h-[84px] md:h-[100px] lg:h-[92px]">
              {/* Hamburger — collapsed (tablet / mobile) only, pinned left as
                  a filled circular button to match the reference layout. */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-[var(--vibe-color-primary)]"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-5 h-0.5 bg-[var(--vibe-color-text-on-primary)] transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-[5px]'
                  }`}
                />
                <span
                  className={`absolute block w-5 h-0.5 bg-[var(--vibe-color-text-on-primary)] transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-5 h-0.5 bg-[var(--vibe-color-text-on-primary)] transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-[5px]'
                  }`}
                />
              </button>

              {/* Logo positioning by breakpoint:
                  • Mobile (< md): CTAs are hidden, so the logo is absolutely
                    centered on the whole bar (dead-center, just the hamburger
                    at left).
                  • Tablet (md–lg): both hamburger (left) and the CTA pair
                    (right) are visible, and the CTAs are much wider than the
                    hamburger. Dead-centering would make the logo look shifted
                    toward the hamburger, so instead the wrapper takes the flex
                    space BETWEEN the two controls (flex-1) and centers the logo
                    within it, then nudges it ~50px to the right (translate-x)
                    so it reads as balanced against the wider CTA group without
                    having to be mathematically centered in the gap.
                  • Desktop (lg+): static, sitting at the left before the nav. */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="md:flex md:flex-1 md:min-w-0 md:justify-center lg:flex-none"
              >
                <a
                  href="/"
                  aria-label="Go to homepage"
                  className="flex-shrink-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:static md:left-auto md:top-auto md:translate-x-[50px] md:translate-y-0 lg:translate-x-0"
                >
                  {logo.image ? (
                    <>
                      {darkTextOnSurface ? <HeaderNavLogoFilter /> : null}
                      <VibesitesImage
                        url={logo.image}
                        alt={logo.alt || 'Community Logo'}
                        className={`h-14 lg:h-16 w-auto max-w-[240px] object-contain${
                          darkTextOnSurface ? ' header-nav-logo-on-light' : ''
                        }`}
                        sizes="240px"
                        priority
                      />
                    </>
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      {propertyName || 'Blue Haven'}
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group — Apply Now + Resident Access (matched filled CTAs). */}
              <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_blank'}
                    rel={applyNowLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="nav-cta-btn group/cta relative hidden md:inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-xs md:tracking-[0.1em] lg:px-6 lg:py-3 lg:text-sm lg:tracking-[0.15em] font-normal tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {applyNowLink.label || 'Apply Now'}
                    </span>
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_blank'}
                    rel={residentAccessLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="nav-cta-btn group/cta relative hidden md:inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-xs md:tracking-[0.1em] lg:px-6 lg:py-3 lg:text-sm lg:tracking-[0.15em] font-normal tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {residentAccessLink.label || 'Resident Access'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Tablet / Mobile Drawer                                         */}
        {/* -------------------------------------------------------------- */}
        {/* A left-anchored panel on the property's primary brand color    */}
        {/* that slides in over a dimming backdrop. Holds a brand heading,  */}
        {/* the full nav list, Apply Now / Resident Access CTAs, social     */}
        {/* icons, and the phone + address. Hidden on lg+ where the inline  */}
        {/* nav renders.                                                    */}
        {/* ============================================================== */}

        {/* Backdrop — dims the page behind the drawer and closes it on tap. */}
        <div
          onClick={closeMobileMenu}
          aria-hidden="true"
          className={`lg:hidden fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${
            menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
        />

        {/* Drawer panel */}
        <div
          className={`mobile-menu-panel lg:hidden fixed left-0 top-0 bottom-0 z-[62] w-[88%] max-w-[440px] overflow-y-auto transition-transform duration-300 ease-out ${
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ backgroundColor: 'var(--vibe-color-primary)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <div className="flex min-h-full flex-col px-9 py-8">
            {/* Close button — circular light disc with a primary-color X. */}
            <button
              onClick={closeMobileMenu}
              aria-label="Close menu"
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[var(--vibe-color-surface)] text-[var(--vibe-color-primary)] transition-colors hover:bg-[var(--vibe-color-surface)]/90"
            >
              <CloseIcon className="h-5 w-5" />
            </button>

            {/* Brand mark — a property logo that links home. Uses the mono
                white/black variant chosen above so it reads on the dark drawer
                fill; falls back to the property name wordmark when the property
                has no logo asset configured. */}
            <EditableSlotGroup
              groupPath="header.drawer_logo"
              definition={runtimeDrawerLogoSlotDef}
              className="mt-10 mb-4"
            >
              <a
                href="/"
                onClick={closeMobileMenu}
                aria-label="Go to homepage"
                className="inline-flex"
              >
                {drawerLogo.image ? (
                  <VibesitesImage
                    url={drawerLogo.image}
                    alt={drawerLogo.alt || propertyName || 'Community Logo'}
                    className="h-12 w-auto max-w-[240px] object-contain"
                    sizes="240px"
                  />
                ) : (
                  <span className="font-heading text-4xl uppercase leading-none tracking-wide text-[var(--vibe-color-text-on-primary)]">
                    {propertyName || 'Blue Haven'}
                  </span>
                )}
              </a>
            </EditableSlotGroup>

            {/* Navigation links */}
            <nav role="navigation" aria-label="Mobile navigation" className="flex flex-col">
              {navLinks.map((link) => (
                <React.Fragment key={link.href || link.label}>
                  <MobileNavLink link={link} onNavigate={closeMobileMenu} />
                  {link.children?.map((child) => (
                    <MobileNavLink
                      key={child.href || child.label}
                      link={child}
                      onNavigate={closeMobileMenu}
                      isChild
                    />
                  ))}
                </React.Fragment>
              ))}
            </nav>

            {/* Utility block — Apply Now / Resident Access, social, phone,
                address. Tight, even vertical rhythm across the items. */}
            <div className="mt-3 space-y-4">
              {/* Apply Now + Resident Access — sit directly below the nav
                  links, above the social icons. Mirror the top-bar CTAs
                  (same slot-group content) as outlined pills on the dark
                  drawer so they match the site-wide CTA shape. */}
              <div className="flex flex-col gap-3">
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_blank'}
                  rel={applyNowLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={closeMobileMenu}
                  className="mobile-util-pill inline-flex w-full items-center justify-center rounded-full border-2 border-[var(--vibe-color-text-on-primary)] px-9 py-3.5 text-sm font-normal uppercase tracking-[0.15em] text-[var(--vibe-color-text-on-primary)] transition-all"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_blank'}
                  rel={residentAccessLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={closeMobileMenu}
                  className="mobile-util-pill inline-flex w-full items-center justify-center rounded-full border-2 border-[var(--vibe-color-text-on-primary)] px-9 py-3.5 text-sm font-normal uppercase tracking-[0.15em] text-[var(--vibe-color-text-on-primary)] transition-all"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </div>

              {/* Social icons — only platforms with a configured URL render.
                  Border thickness matches the util pills (2px). */}
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-4">
                  {socialLinks.map(({ url, icon, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[var(--vibe-color-text-on-primary)] text-[var(--vibe-color-text-on-primary)] transition-colors hover:bg-[var(--vibe-color-text-on-primary)]/10"
                    >
                      <FontAwesomeIcon icon={icon} className="h-4 w-4" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              )}

              {/* Phone */}
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref block text-base font-semibold text-[var(--vibe-color-text-on-primary)] transition-colors"
              >
                {/* EditableToken wraps just the inner text span so the editor
                    can target the phone number from inside the open drawer. */}
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="rise-nav-darken-hover"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>

              {/* Address */}
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="block text-base leading-relaxed text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
                >
                  <span className="rise-nav-darken-hover">
                    <span className="block">{addressLine1}</span>
                    {hasValue(addressLine2) && <span className="block">{addressLine2}</span>}
                  </span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        /* The header is a solid light (surface) bar on every page, sitting
           above the hero in normal flow (sticky). A subtle bottom border +
           faint shadow separate it from the content below. */
        .header-bar {
          border-bottom: 1px solid var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }

        /* Light-surface header: keep the red bar + its white label, paint the
           rest of the primary logo black so a white wordmark is visible. */
        .header-nav-logo-on-light {
          filter: url(#${HEADER_NAV_LOGO_FILTER_ID});
        }

        /* "More" / child dropdown panels sit on --vibe-color-background, whose
           contrast side may differ from the surface bar, so their links use the
           dedicated --bh-header-dropdown-fg foreground at rest (dark theme text
           on a light background, white on a dark one). The !important beats the
           per-link Tailwind text color utilities (including the muted child
           rows) so the dropdown-fg wins over any inline text-* utility. Note
           that we deliberately DO NOT include :hover here — the hover rule
           below owns the hovered color so the secondary-color hover swap can
           come through. */
        .header-bar .nav-more-dropdown a {
          color: var(--bh-header-dropdown-fg) !important;
        }

        /* Dropdown item hover — reference: mckinneyvillageapts.com. Dropdown
           rows read as flat list items that shade on hover (background fill,
           no underline), not as underlined nav links. So inside the dropdown
           panel we cancel the top-and-bottom underline/overline the top-level
           nav uses, and swap in a subtle background-fill hover instead.
           color-mix against --vibe-color-text (rather than a hardcoded gray)
           keeps the tint readable on both light and dark theme backgrounds.
           The hovered LABEL takes on --vibe-color-secondary — the property's
           accent — so the row also reads as active via a color pop, not just
           the background shift. Marked !important to beat the resting
           dropdown-fg rule directly above (which also uses !important). */
        .header-bar .nav-more-dropdown a.rise-nav-darken-hover {
          text-decoration-line: none;
          text-decoration-color: transparent;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        .header-bar .nav-more-dropdown a.rise-nav-darken-hover:hover {
          background-color: color-mix(in srgb, var(--vibe-color-text) 8%, transparent);
          color: var(--vibe-color-secondary) !important;
          text-decoration-line: none;
          text-underline-offset: 6px;
        }

        /* The tablet / mobile drawer sits on --vibe-color-primary (the dark
           brand color), so its links + buttons render in the on-primary
           (usually white) text color. The hover affordance for nav links is
           the animated underline from .rise-nav-darken-hover below (drawn in
           currentColor), so we hold the text color steady on hover. The close
           button is the deliberate exception — it's a light disc with a
           primary-color glyph, so we scope these rules to anchors + the
           inner text and let the close button keep its own colors. */
        .mobile-menu-panel a {
          color: var(--vibe-color-text-on-primary);
        }
        .mobile-menu-panel a:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* Nav links: animated underline on hover (used by Header
           DesktopNavLink, "More" dropdown items, MobileNavLink, and the
           Navigation sub-bar). All of these contexts render against a
           white surface, so the affordance is an underline rather than a
           color shift — the underline is drawn via text-decoration so it
           rides the text baseline and works at any link padding. The
           transparent-header (over-hero) brightening hover above keeps
           its own color shift via earlier !important rules; the
           underline simply layers on top with currentColor. */
        .rise-nav-darken-hover {
          /* underline + overline so a matching top AND bottom line appear on
             hover. Both share text-decoration-color, so they fade in together
             (transparent → currentColor) only on hover. */
          text-decoration-line: underline overline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Top-level desktop nav items draw their top + bottom hover lines as
           ::before / ::after pseudo-elements (rather than borders) so they can
           ANIMATE into place — matching the reference site's livelier effect.
           The lines sit at the edges of the link's vertical padding (py-1.5 ≈
           6px from the label). On hover they animate "outside in": the top
           line starts above and slides DOWN, the bottom line starts below and
           slides UP, both settling onto the label with an ease-out. */
        .nav-tb-hover {
          position: relative;
        }
        .nav-tb-hover::before,
        .nav-tb-hover::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: currentColor;
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }
        /* Resting at the padding edges; start offsets pull each line to the
           OUTSIDE (top above, bottom below) so they converge inward on hover. */
        .nav-tb-hover::before { top: 0; transform: translateY(-7px); }
        /* Bottom line sits 2px lower than the padding edge so it clears the
           label's descenders a touch more than the top line. */
        .nav-tb-hover::after { bottom: -2px; transform: translateY(9px); }
        .nav-tb-hover:hover::before,
        .nav-tb-hover:hover::after {
          opacity: 1;
          transform: translateY(0);
        }
        /* Active page: keep BOTH lines lit persistently (same resting position
           as the hover end-state) so the current banner/interior page reads as
           selected in the nav — a top bar above and a bottom bar below the
           label. This is the same double-bar the link shows on hover, just
           held on. Applied to the matching top-level link (and the "More"
           button when the active page is in overflow) via .nav-tb-active. */
        .nav-tb-active::before,
        .nav-tb-active::after {
          opacity: 1;
          transform: translateY(0);
        }
        /* Address / phone rows in the mobile drawer wear the
           rise-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .rise-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .rise-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Outlined util pills inside the drawer — on the dark primary
           background they fill with a faint on-primary wash on hover while
           keeping their on-primary label. */
        .mobile-menu-panel .mobile-util-pill:hover {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 12%, transparent);
          color: var(--vibe-color-text-on-primary);
        }

        /* ================================================================ */
        /* CTA Button — filled pill                                        */
        /* ================================================================ */
        /* Solid primary fill with an on-primary label, fully rounded. */
        .nav-cta-btn {
          background: var(--vibe-color-primary);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
          color: var(--vibe-color-text-on-primary);
          box-shadow: none;
        }
        .nav-cta-btn:hover {
          background: color-mix(in srgb, var(--vibe-color-primary), black 12%);
          border-color: color-mix(in srgb, var(--vibe-color-primary), black 12%);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Optional extra signal to expose the Interactive Map page in the Find Your Home dropdown. ApartmentSync and Engrain SightMap fields are detected automatically; bind this token to another field (or enter a literal) only when you need a custom override. Leave blank to rely on automatic detection.',
      defaultValue: '',
      suggestedFields: [
        'custom_engrain_sightmap_id',
        'custom_engrain_sitemap_id',
        'custom_apartmentsync_javascript_id',
      ],
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.drawer_logo': {
      label: 'Drawer Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_white_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          defaultValue: '/resident-access',
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
